import React from 'react';

import {IconSizeType} from 'common/constants';
import {ButtonReact} from 'common/components/button';
import {ButtonTheme} from 'common/components/button/constants';
import {SvgIconReact} from 'common/components/svg-icon';
import {getProfilePath} from 'common/report/util/profile/utils';
import {withStyles} from 'common/util/react';
import {react2panel} from 'common/util/react2panel';
import {useTranslation} from 'common/i18n/hooks';

import {TrackedUserIconReact} from '../tracked-user-icon';
import {RecordingEventMetadata, RecordingMetadata} from './types';
import styles from './index.module.styl';
import css from './index.module.styl?inline';
import {getEventCount, getFormattedDuration, getReplayEventCount, getSearchHighlightPieces} from './util';

type ReplayPlaylistRowProps = {
  sortedReplays: RecordingMetadata[];
  currentRecordingIdx: number;
  hasBeenPlayed: boolean;
  replay: RecordingMetadata;
  replayEvents: RecordingEventMetadata[];
  displayName: string;
  displayTime: string;
  searchFilter: string;
  isExpanded: boolean;
  onToggleExpand: (replayId: string) => void;
};

function replayPlaylistRow(props: ReplayPlaylistRowProps, ref: React.Ref<HTMLDivElement>) {
  const {t, print} = useTranslation();

  function maybeShowSearchHighlight(inputText: string) {
    const pieces = getSearchHighlightPieces(inputText, props.searchFilter);

    return (
      <>
        {pieces.map((piece, i) => {
          return (
            <span className={piece.highlight ? styles.replaysTextHighlight : ``} key={i}>
              {piece.text}
            </span>
          );
        })}
      </>
    );
  }

  const eventCount = getReplayEventCount(props.replayEvents);
  const eventsLoading = props.replayEvents === undefined;
  const isReplaySelected = props.replay.replayId === props.sortedReplays[props.currentRecordingIdx].replayId;

  return (
    <div className={`${styles.replaysListItem} ${isReplaySelected ? styles.replaysListItemSelected : ``}`} ref={ref}>
      <div className={styles.replaysListItemInfo}>
        <a href={getProfilePath(props.replay.distinctId, null)} target='_blank'>
          <TrackedUserIconReact
            className={styles.replaysListItemUserIcon}
            distinctId={props.replay.distinctId}
            imgSrc={props.replay.avatarUrl}
            email={props.replay.email}
            size={IconSizeType.Small}
            enableOutline={isReplaySelected}
          />
        </a>
        <div className={styles.replaysListItemText}>
          <div className={styles.replaysListItemTextTitle}>{maybeShowSearchHighlight(props.displayName)}</div>
          <div className={styles.replaysListItemTextSubtitle}>
            {maybeShowSearchHighlight(props.displayTime)}
            {` `}• {print(getEventCount(eventCount, eventsLoading))} •{` `}
            {print(getFormattedDuration(props.replay.duration))}
          </div>
        </div>
      </div>
      <div className={styles.replaysListItemIndicators}>
        {props.hasBeenPlayed && <SvgIconReact className={styles.replaysListItemPlayedIcon} icon={`circle-checkmark`} />}
        <ButtonReact
          theme={ButtonTheme.Tertiary}
          icon={props.isExpanded ? `caret-up` : `caret-down`}
          onClick={(ev) => {
            props.onToggleExpand(props.replay.replayId);
            ev.stopPropagation();
          }}
          tooltip={{
            text: props.isExpanded ? t(`session-replay.collapse-replay`) : t(`session-replay.expand-replay`),
          }}
        />
      </div>
    </div>
  );
}

const ReplayPlaylistRowStyled = withStyles(React.forwardRef(replayPlaylistRow), css);
export const ReplayPlaylistRowEl = react2panel(`mp-replay-playlist-row`, ReplayPlaylistRowStyled);
